'use client';

import { useCustomer, useLogout } from '@adapters/bigcommerce/hooks/customer';
import { useLocale } from '@hooks/useLocale';
import { useTranslations } from 'next-intl';
import SimpleLink from '_components/UI/SimpleLink';
import TextButton from '_components/UI/TextButton';
import { AccountTabs } from '@components/account/types';
import { getLoyaltyFeatureFlag } from '@config/feature-flag';
import { useLayoutEffect, useState } from 'react';
import IconButton from '_components/UI/IconButton';
import { XIcon } from '@phosphor-icons/react';
import {
  AccountMenuLevels,
  trackAccountMenuEvents,
} from '_utils/GA4/trackAccountMenuEvents';

import styles from './LoggedInContent.module.scss';

const LoggedInContent = ({ onClose }: { onClose?: () => void }) => {
  const [loyaltyFeatureFlag, setLoyaltyFeatureFlag] = useState(false);
  useLayoutEffect(() => {
    setLoyaltyFeatureFlag(getLoyaltyFeatureFlag());
  }, []);
  const t = useTranslations('common');
  const { customer } = useCustomer();
  const locale = useLocale();
  const navigationLinks = [
    {
      label: 'nav.utility.account.new.order',
      path: '/my-account?p=orders',
    },
    {
      label: 'nav.utility.account.new.rewards',
      path: `/my-account?p=${AccountTabs.MemberRewards}`,
    },
    {
      label: 'nav.utility.account.new.addresses',
      path: '/my-account?p=addresses',
    },
    {
      label: 'nav.utility.account.new.paymentMethods',
      path: '/my-account?p=payment-methods',
    },
    {
      label: 'nav.utility.account.new.giftCards',
      path: `/my-account?p=${AccountTabs.GiftCards}`,
    },
    {
      label: 'nav.utility.account.new.accountSettings',
      path: '/my-account',
    },
  ].flat();

  const handleAccountMenuClick = (label: string, path: string) => {
    trackAccountMenuEvents({
      data: {
        level: AccountMenuLevels.level_2,
        linkUrl: path,
        linkText: t(label),
      },
    });
  };

  const logout = useLogout();

  const handleLogout = () => {
    trackAccountMenuEvents({
      data: {
        level: AccountMenuLevels.level_2,
        linkUrl: null,
        linkText: t('nav.utility.account.signout'),
      },
    });
    logout();
  };

  return (
    <>
      {onClose && (
        <div className={styles.closeButton}>
          <IconButton
            icon={XIcon}
            type="transparent"
            aria-label={t('close')}
            onClick={onClose}
            buttonType="button"
            iconSize={16}
            size="sm"
            iconWeight="light"
          />
        </div>
      )}
      <div className={styles.content}>
        <h2 className={styles.greetingHeading}>
          {t('nav.utility.account.greeting.whatOutdoorActivity', {
            firstName: customer?.firstName || '',
          })}
        </h2>
        <nav className={styles.navigation} role="navigation">
          <ul className={styles.navList}>
            {navigationLinks.map((link) => (
              <li key={link.label}>
                <SimpleLink
                  href={`/${locale}${link.path}`}
                  className={styles.navLink}
                  onClick={() => handleAccountMenuClick(link.label, link.path)}
                >
                  <span className={styles.navLinkText}>{t(link.label)}</span>
                </SimpleLink>
              </li>
            ))}
          </ul>
        </nav>
        <TextButton
          label={t('nav.utility.account.signout')}
          size="large"
          type="secondary"
          className={styles.signOutButton}
          onClick={handleLogout}
        />
      </div>
    </>
  );
};

export default LoggedInContent;
